import { Card, Col, Row, Spin, Typography } from "antd";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as actions from "../../../../actions/eComm/eCommRetailFSAActions";
import * as selectors from "../../../../reducer/ECommRetail/selectors";
import { IBusinessPlan, IKBI, KBIInputState } from "../../../../reducer/ECommRetail/_interfaces";

type IProps = {
    inputDisabled: boolean;
}

const KBIS_NO_BP = [
    'KEECOM027',
    'KEECOM028'
]

const KBICard:FC<IProps> = ({inputDisabled}) => {
    const dispatch = useDispatch();

    const kbiMaster:IKBI[] = useSelector(selectors.kbiMaster);
    const kbiInputUnedited = useSelector(selectors.kbiMasterUnedited);
    const kbiValuesLoading:boolean = useSelector(selectors.kbiValuesLoading);
    const kbiValues:KBIInputState = useSelector(selectors.kbiValues);
    const kbiValuesPrevPeriod:KBIInputState = useSelector(selectors.kbiValuesPrevPeriod);
    const businessPlan:IBusinessPlan = useSelector(selectors.businessPlan);


    const [totalVolumeValue, setTotalVolumeValue] = useState<number>(0);
    const [totalNSRValue, setTotalNSRValue] = useState<number>(0);
    const [totalVolumePrevValue, setTotalVolumePrevValue] = useState<number>(0);
    const [totalNSRValuePrev, setTotalNSRValuePrev] = useState<number>(0);

    const [totalBPVolume, setTotalBPVolume] = useState<number>(0);
    const [totalBPNSR, setTotalBPNSR] = useState<number>(0);

    const [totalREVolume, setTotalREVolume] = useState<number>(0);
    const [totalRENSR, setTotalRENSR] = useState<number>(0);

    useEffect(() => {
        const { totalNSR, totalNSRPrev, totalNSRBP, totalNSRRE } = getTotalNSR();
        const { totalVolume, totalVolumePrev, totalVolumeBP, totalVolumeRE } = getTotalVolume();

        setTotalVolumeValue(totalVolume);
        setTotalNSRValue(totalNSR);

        setTotalVolumePrevValue(totalVolumePrev);
        setTotalNSRValuePrev(totalNSRPrev);
        
        setTotalRENSR(totalNSRRE);
        setTotalREVolume(totalVolumeRE);

        setTotalBPVolume(totalVolumeBP);
        setTotalBPNSR(totalNSRBP);

    }, [kbiValues, kbiValuesPrevPeriod, businessPlan]);

    //Dynamic Input onChange Handler
    const inputOnChange = (e:React.ChangeEvent<HTMLInputElement>) => {
        const re = /^[0-9]*\.?[0-9]*$/;
        const value = e.target.value;
        const name = e.target.name;

        if (value === "" || re.test(value)) {
            dispatch(actions.setKBIInputValues({...kbiValues, [name]: value} as any));
            dispatch(actions.setKBIUnedited({ ...kbiInputUnedited, [name]: value }));
        }
    };

    const getTotalNSR = () => {
        let totalNSR = 0;
        let totalNSRPrev = 0;
        let totalNSRBP = 0;
        let totalNSRRE = 0;
        

        ["KEECOM001", "KEECOM002", "KEECOM003", "KEECOM004", "KEECOM025"].forEach((kbi_id) => {
            totalNSR = totalNSR + parseFloat(String(kbiValues[kbi_id]));
            totalNSRPrev = totalNSRPrev + parseFloat(String(kbiValuesPrevPeriod[kbi_id]));
            totalNSRBP = totalNSRBP + businessPlan?.nsrData[kbi_id]?.value;
            totalNSRRE = totalNSRRE + (businessPlan?.losNsrData[kbi_id]?.value > 0 ? businessPlan?.losNsrData[kbi_id]?.value : 0)
        });

        return { totalNSR, totalNSRPrev, totalNSRBP, totalNSRRE };
    };
    
    const getTotalVolume = () => {
        let totalVolume = 0;
        let totalVolumePrev = 0;
        let totalVolumeBP = 0;
        let totalVolumeRE = 0;

        ["KEECOM005", "KEECOM006", "KEECOM007", "KEECOM008", "KEECOM026"].forEach((kbi_id) => {
            totalVolume = totalVolume + parseFloat(String(kbiValues[kbi_id]));
            totalVolumePrev = totalVolumePrev + parseFloat(String(kbiValuesPrevPeriod[kbi_id]));
            totalVolumeBP = totalVolumeBP + (businessPlan?.volumeData[kbi_id]?.value || 0);
            totalVolumeRE = totalVolumeRE + (businessPlan?.losVolumeData[kbi_id]?.value > 0 ? businessPlan?.losVolumeData[kbi_id]?.value : 0)
        });

        return {totalVolume, totalVolumePrev, totalVolumeBP, totalVolumeRE};
    };

    const getRelDiff = (num_one:number, num_two:number) => {
        return 100 * Math.abs((num_one - num_two) / ((num_one + num_two) / 2))
    }

    const getChange = (num_one:number | null, num_two:number | null) => {
        if (num_one != null && num_two != null) {
            return (num_one - num_two) / num_two * 100
        }
        return NaN;
    }

    return (
        <Col span={12}>
            <Spin tip="Loading" spinning={kbiValuesLoading} size="large">
                <Card>
                    <Row style={{ height: 60 }}>
                        <Col span={14} />
                        <Col span={4}>
                            <p className="user-view-kbi-unit">vs. PY</p>
                        </Col>
                        <Col span={3}>
                            <p className="user-view-kbi-unit">vs. BP</p>
                        </Col>
                        <Col span={3}>
                            <p className="user-view-kbi-unit">vs. RE</p>
                        </Col>
                    </Row>
                    {kbiMaster.map((kbi) => {
                        const bpTargetData = 
                            businessPlan?.nsrData[kbi.kbi_id]
                            || businessPlan?.volumeData[kbi.kbi_id];
                        
                        const losTargetData = 
                            businessPlan?.losNsrData[kbi.kbi_id]
                            || businessPlan?.losVolumeData[kbi.kbi_id];
                        
                        return (
                            kbi.kbi_id === "KEECOM009" ? (
                                <Row style={{ height: "60px" }}>
                                    <Col span={4}>
                                        <p className="user-view-input-label-text">
                                            {kbi.title}
                                        </p>
                                    </Col>
                                    <Col span={7}>
                                        <input
                                            className={`text-input-box ${inputDisabled ? 'text-input-box-disabled' : ''}`}
                                            name={kbi.kbi_id}
                                            value={
                                                kbiValues?.[kbi.kbi_id]
                                                    ? kbiValues[kbi.kbi_id]!
                                                    : ""
                                            }
                                            onChange={inputOnChange}
                                            disabled={inputDisabled}
                                            type="text"
                                            step="any"
                                            min="0"
                                        />
                                    </Col>
                                    <Col span={3}>
                                        <p className="user-view-kbi-unit">{kbi.unit}</p>
                                    </Col>
                                    <Col span={4}>
                                        <p className="user-view-kbi-unit">
                                            {kbiValuesPrevPeriod[kbi.kbi_id] != null && kbiValuesPrevPeriod[kbi.kbi_id]! > -1
                                                && !isNaN(getChange(kbiValues[kbi.kbi_id], kbiValuesPrevPeriod[kbi.kbi_id]))
                                                && isFinite(getChange(kbiValues[kbi.kbi_id], kbiValuesPrevPeriod[kbi.kbi_id]))
                                                ? `${getChange(kbiValues[kbi.kbi_id], kbiValuesPrevPeriod[kbi.kbi_id]).toFixed(2)}%`
                                                : 'N/A'}
                                        </p>
                                    </Col>
                                    <Col span={3}>
                                        <p className="user-view-kbi-unit">
                                            {bpTargetData?.value > -1
                                                && !isNaN(getChange(kbiValues[kbi.kbi_id], bpTargetData.value))
                                                && isFinite(getChange(kbiValues[kbi.kbi_id], bpTargetData.value))
                                                ? `${getChange(kbiValues[kbi.kbi_id], bpTargetData.value).toFixed(2)}%`
                                                : 'N/A'}
                                        </p>
                                    </Col>
                                    <Col span={3}>
                                        <p className="user-view-kbi-unit">
                                            {losTargetData?.value > -1
                                                && !isNaN(getChange(kbiValues[kbi.kbi_id], losTargetData.value))
                                                && isFinite(getChange(kbiValues[kbi.kbi_id], losTargetData.value))
                                                ? `${getChange(kbiValues[kbi.kbi_id], losTargetData.value).toFixed(2)}%`
                                                : 'N/A'}
                                        </p>
                                    </Col>
                                </Row>
                            ) : (
                                <Row style={{ height: "60px" }}>
                                    <Col span={4}>
                                        <p className="user-view-input-label-text">
                                            {kbi.title}
                                        </p>
                                    </Col>
                                    <Col span={7}>
                                        <input
                                            className={`text-input-box ${inputDisabled ? 'text-input-box-disabled' : ''}`}
                                            name={kbi.kbi_id}
                                            value={
                                                kbiValues?.[kbi?.kbi_id] != null && kbiValues?.[kbi?.kbi_id]! > -1
                                                    ? kbiValues[kbi.kbi_id]!
                                                    : ''
                                            }
                                            onChange={inputOnChange}
                                            disabled={inputDisabled}
                                            type="text"
                                            step="any"
                                            min="0"
                                        />
                                    </Col>
                                    <Col span={3}>
                                        <p className="user-view-kbi-unit">{kbi.unit}</p>
                                    </Col>
                                    <Col span={4}>
                                        <p className="user-view-kbi-unit">
                                            {!isNaN(getChange(kbiValues[kbi.kbi_id], kbiValuesPrevPeriod[kbi.kbi_id])) && isFinite(getChange(kbiValues[kbi.kbi_id], kbiValuesPrevPeriod[kbi.kbi_id]))
                                                ? `${getChange(kbiValues[kbi.kbi_id], kbiValuesPrevPeriod[kbi.kbi_id]).toFixed(2)}%`
                                                : 'N/A'}

                                            {/* {getRelDiff(kbiValues[kbi.kbi_id], kbiValuesPrevPeriod[kbi.kbi_id])} */}

                                        </p>
                                    </Col>
                                    <Col span={3}>
                                        {KBIS_NO_BP.indexOf(kbi.kbi_id) < 0 && <p className="user-view-kbi-unit">
                                            {!isNaN(getChange(kbiValues[kbi.kbi_id], bpTargetData?.value)) && isFinite(getChange(kbiValues[kbi.kbi_id], bpTargetData?.value))
                                                ? `${getChange(kbiValues[kbi.kbi_id], bpTargetData?.value).toFixed(2)}%`
                                                : 'N/A'}
                                        </p>}
                                    </Col>
                                    <Col span={3}>
                                        {KBIS_NO_BP.indexOf(kbi.kbi_id) < 0 && <p className="user-view-kbi-unit">
                                            {!isNaN(getChange(kbiValues[kbi.kbi_id], losTargetData?.value)) && isFinite(getChange(kbiValues[kbi.kbi_id], losTargetData?.value))
                                                ? `${getChange(kbiValues[kbi.kbi_id], losTargetData?.value).toFixed(2)}%`
                                                : 'N/A'}
                                        </p>}
                                    </Col>
                                </Row>
                            )
                        )
                    })}
                    {/* Total NSR(Only To Show) */}
                    <Row style={{ height: "60px" }}>
                        <Col span={4}>
                            <p className="user-view-input-label-text">
                                Total NSR
                            </p>
                        </Col>
                        <Col span={7}>
                            <input
                                className="text-input-box-disabled"
                                name="total_nsr"
                                value={totalNSRValue || 0}
                                // onChange={inputOnChange}
                                disabled={true}
                                type="number"
                                step="any"
                                min="0"
                            />
                        </Col>
                        <Col span={3}>
                            <p className="user-view-kbi-unit">('000 EUR)</p>
                        </Col>
                        <Col span={4}>
                            <p className="user-view-kbi-unit">
                                {!isNaN(getChange(totalNSRValue, totalNSRValuePrev)) && isFinite(getChange(totalNSRValue, totalNSRValuePrev))
                                    ? `${getChange(totalNSRValue, totalNSRValuePrev).toFixed(2)}%`
                                    : 'N/A'}
                            </p>
                        </Col>
                        <Col span={3}>
                            <p className="user-view-kbi-unit">
                                {!isNaN(getChange(totalNSRValue, totalBPNSR)) && isFinite(getChange(totalNSRValue, totalBPNSR))
                                    ? `${getChange(totalNSRValue, totalBPNSR).toFixed(2)}%`
                                    : 'N/A'}
                            </p>
                        </Col>
                        
                        <Col span={3}>
                            <p className="user-view-kbi-unit">
                                {!isNaN(getChange(totalNSRValue, totalRENSR)) && isFinite(getChange(totalNSRValue, totalRENSR))
                                    ? `${getChange(totalNSRValue, totalRENSR).toFixed(2)}%`
                                    : 'N/A'}
                            </p>
                        </Col>
                    </Row>
                    {/* Total Volume(Only To Show) */}
                    <Row style={{ height: "60px" }}>
                        <Col span={4}>
                            <p className="user-view-input-label-text">
                                Total Volume
                            </p>
                        </Col>
                        <Col span={7}>
                            <input
                                className="text-input-box-disabled"
                                name="total_nsr"
                                value={totalVolumeValue || 0}
                                // onChange={inputOnChange}
                                disabled={true}
                                type="number"
                                step="any"
                                min="0"
                            />
                        </Col>
                        <Col span={3}>
                            <p className="user-view-kbi-unit">(UC '000)</p>
                        </Col>
                        <Col span={4}>
                            <p className="user-view-kbi-unit">
                                {!isNaN(getChange(totalVolumeValue, totalVolumePrevValue)) && isFinite(getChange(totalVolumeValue, totalVolumePrevValue))
                                    ? `${getChange(totalVolumeValue, totalVolumePrevValue).toFixed(2)}%`
                                    : 'N/A'}
                            </p>
                        </Col>
                        
                        <Col span={3}>
                            <p className="user-view-kbi-unit">
                                {!isNaN(getChange(totalVolumeValue, totalBPVolume)) && isFinite(getChange(totalVolumeValue, totalBPVolume))
                                    ? `${getChange(totalVolumeValue, totalBPVolume).toFixed(2)}%`
                                    : 'N/A'}
                            </p>
                        </Col>
                        
                        <Col span={3}>
                            <p className="user-view-kbi-unit">
                                {!isNaN(getChange(totalVolumeValue, totalREVolume)) && isFinite(getChange(totalVolumeValue, totalREVolume))
                                    ? `${getChange(totalVolumeValue, totalREVolume).toFixed(2)}%`
                                    : 'N/A'}
                            </p>
                        </Col>
                    </Row>
                </Card>
            </Spin>
        </Col>
    );
}

export default KBICard;